import { createDraftSafeSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { GlobalState } from '@store'
import { LoadingState } from '@types'

export type LoginStep =
  | 'IDLE'
  | 'REQUESTING_OTP'
  | 'OTP_REQUIRED'
  | 'OTP_EXPIRED'
  | 'VERIFYING_OTP'
  | 'AUTHENTICATED'
  | 'FAILED'

export interface LoginState {
  cocap: string
  isLogged: boolean
  loadingStatus: LoadingState
  step: LoginStep
  message: string
  userId: string
  isVerified: boolean
  challengeId: string
  email: string
  expiresAt: number
  resendAt: number
}

export const initialState: LoginState = {
  cocap: '',
  isLogged: false,
  loadingStatus: 'NOT_STARTED',
  step: 'IDLE',
  message: '',
  userId: '',
  isVerified: false,
  challengeId: '',
  email: '',
  expiresAt: 0,
  resendAt: 0
}

export type LoginAction = PayloadAction<{ username: string; password: string }>
export type VerifyOtpAction = PayloadAction<{
  challengeId: string
  otp: string
}>
type OtpRequestedAction = PayloadAction<{
  challengeId: string
  email: string
  expiresAt: number
  resendAt: number
  message: string
}>

const loginSlice = createSlice({
  name: 'login',
  initialState,
  reducers: {
    login: (state, _action: LoginAction) => ({
      ...state,
      loadingStatus: 'LOADING',
      step: state.step === 'OTP_EXPIRED' ? 'OTP_EXPIRED' : 'REQUESTING_OTP',
      message: ''
    }),
    otpRequested: (state, action: OtpRequestedAction) => ({
      ...state,
      loadingStatus: 'SUCCESS',
      step: 'OTP_REQUIRED',
      isLogged: false,
      challengeId: action.payload.challengeId,
      email: action.payload.email,
      expiresAt: action.payload.expiresAt,
      resendAt: action.payload.resendAt,
      message: action.payload.message
    }),
    verifyOtp: (state, _action: VerifyOtpAction) => ({
      ...state,
      loadingStatus: 'LOADING',
      step: 'VERIFYING_OTP',
      message: ''
    }),
    loginSuccess: (state, action) => ({
      ...state,
      loadingStatus: 'SUCCESS',
      step: 'AUTHENTICATED',
      isLogged: true,
      cocap: action.payload.user,
      userId: action.payload.userId,
      isVerified: action.payload.isVerified,
      challengeId: '',
      expiresAt: 0,
      resendAt: 0,
      message: ''
    }),
    loginFailed: (state, action: PayloadAction<string>) => ({
      ...state,
      loadingStatus: 'FAILED',
      step: state.step === 'OTP_EXPIRED' ? 'OTP_EXPIRED' : state.challengeId ? 'OTP_REQUIRED' : 'FAILED',
      isLogged: false,
      message: action.payload
    }),
    expireOtp: state => ({
      ...state,
      loadingStatus: 'SUCCESS',
      step: 'OTP_EXPIRED',
      challengeId: '',
      expiresAt: 0,
      resendAt: 0,
      message: ''
    }),
    resetOtp: state => ({
      ...initialState,
      cocap: state.cocap,
      userId: state.userId,
      isVerified: state.isVerified
    }),
    clearLogin: () => {
      localStorage.removeItem('persist:root')
      return initialState
    }
  }
})

export const selectIsLoading = createDraftSafeSelector([(state: GlobalState) => state.login], loginState => ({
  isLoading: loginState.loadingStatus === 'LOADING',
  isFailed: loginState.loadingStatus === 'FAILED',
  isLogged: loginState.isLogged,
  message: loginState.message
}))

export const selectLoginFlow = createDraftSafeSelector([(state: GlobalState) => state.login], loginState => ({
  step: loginState.step,
  challengeId: loginState.challengeId,
  email: loginState.email,
  expiresAt: loginState.expiresAt,
  resendAt: loginState.resendAt
}))

export const selectCredentials = createDraftSafeSelector(
  [
    (state: GlobalState) => state.login.cocap,
    (state: GlobalState) => state.login.userId,
    (state: GlobalState) => state.login.isVerified
  ],
  (cocap, userId, isVerified) => ({ cocap, userId, isVerified })
)

export const {
  login,
  otpRequested,
  verifyOtp,
  loginSuccess,
  loginFailed,
  expireOtp,
  resetOtp,
  clearLogin
} = loginSlice.actions
export default loginSlice.reducer
